import { getLiquidityClassification } from "components/charts/modern/TradesChart";
import IconCard, { CardDescription, CardValue } from "components/company/cards/IconCard";
import classNames from "components/ui/classNames";
import { tailwindColors } from "components/ui/colors/colorsBundle";
import { TextTooltip } from "components/ui/Tooltip";
import type { IBrazilianCompanyIssuerTopLevelFragment } from "lib/fragments/__generated__/BrazilianCompanyIssuerTopLevel.generated";
import type { IBrazilianRealEstateFundTopLevelFragment } from "lib/fragments/__generated__/BrazilianRealEstateFundTopLevel.generated";
import { ChartSplineIcon } from "lucide-react";
import { FormattedMessage, useIntl } from "react-intl";

interface ILiquidityScoreCardProps {
  tickerSymbol: string;
  issuer: IBrazilianCompanyIssuerTopLevelFragment | IBrazilianRealEstateFundTopLevelFragment;
}

export default function LiquidityScoreCard({ tickerSymbol, issuer }: ILiquidityScoreCardProps) {
  const intl = useIntl();

  const liquidityScore =
    issuer.__typename === "BrazilianCompanyIssuer"
      ? issuer.securities.find((candidate) => candidate.tickerSymbol === tickerSymbol)?.liquidityScore
      : issuer.liquidityScore;

  if (!liquidityScore) {
    return null;
  }

  const liquidityClassification = getLiquidityClassification(intl, liquidityScore.calculatedScore);
  const scoreFormatter = new Intl.NumberFormat(intl.locale, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });

  return (
    <IconCard
      primaryColor={tailwindColors.purple[600]}
      secondaryColor={tailwindColors.purple[100]}
      icon={<ChartSplineIcon size={14} strokeWidth={1.5} absoluteStrokeWidth={true} />}
    >
      <CardValue
        className={classNames("m-0 line-clamp-1 font-aeonik text-2xl font-bold", liquidityClassification.className)}
      >
        <TextTooltip
          content={intl.formatMessage({
            id: "3DCXlL",
            defaultMessage: `O Índice de Liquidez quantifica, em uma escala de 0 a 10, quão fácil é para um investidor pessoa física entrar e sair de uma posição sem sofrer impacto significativo de preço. O índice combina a Mediana 30 dias do Volume Financeiro (R$) e da Quantidade de Negócios usando escala logarítmica, refletindo a natureza exponencial da liquidez.`,
          })}
        >
          <span>
            {scoreFormatter.format(liquidityScore.calculatedScore)} • {liquidityClassification.label}
          </span>
        </TextTooltip>
      </CardValue>
      <CardDescription>
        <FormattedMessage
          defaultMessage="Índice de Liquidez • {tickerSymbol}"
          id="am9uNV"
          values={{
            tickerSymbol: liquidityScore.tickerSymbol,
          }}
        />
      </CardDescription>
    </IconCard>
  );
}
